import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import GoogleAlalytics from '../../../components/GoogleAnalytics'
import NewVsReturningUsers from '../../../components/NewVsReturningUser'
import NewUsersByChannel from '../../../components/NewUsersByChannel'
import { FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material'
import LandingPageSessions from '../../../components/LandingPageSessions'
import ActiveUsersByOS from '../../../components/ActiveUserByOs'
import LoadingScreen from '../../../components/LoadingScreen'
import api from '../../../services/Axios'
import UserFlowDfd from '../../../components/UserFlowDfd'

const formatDate = (date) => date.toISOString().slice(0, 10)

const getRange = (preset) => {
    const end = new Date()
    const start = new Date(end)
    const days = { days: 7, weekly: 7, monthly: 30, yearly: 365 }[preset]

    if (days) start.setDate(start.getDate() - days + 1)
    return { start_date: formatDate(start), end_date: formatDate(end) }
}

const Reports = () => {
    const [preset, setPreset] = useState('days')
    const [dateRange, setDateRange] = useState(() => getRange('days'))
    const [loadingRequests, setLoadingRequests] = useState(() => new Set())
    const [userFlowEvents, setUserFlowEvents] = useState([])
    const handlePresetChange = (event) => {
        const nextPreset = event.target.value
        setPreset(nextPreset)
        if (nextPreset !== 'custom') setDateRange(getRange(nextPreset))
    }

    const handleLoadingChange = useCallback((requestId, isLoading) => {
        setLoadingRequests((requests) => {
            const nextRequests = new Set(requests)
            if (isLoading) nextRequests.add(requestId)
            else nextRequests.delete(requestId)
            return nextRequests
        })
    }, [])

    const reportProps = useMemo(
        () => ({ dateRange, onLoadingChange: handleLoadingChange }),
        [dateRange, handleLoadingChange]
    )

    useEffect(() => {
        handleLoadingChange('onboarding-events', true)
        api.get('/admin/analytics/ga/onboarding-events', { params: dateRange })
            .then((response) => setUserFlowEvents(response?.data || {}))
            .catch((error) => console.error('Error fetching user onboarding events:', error))
            .finally(() => handleLoadingChange('onboarding-events', false))
    }, [dateRange, handleLoadingChange])
    return (
        <>
            <Helmet>
                <title>Reports</title>
            </Helmet>
            <div className="dashboard-main">
                <h1 className="dashboard-title">User Reports</h1>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="report-period-label">Period</InputLabel>
                        <Select
                            labelId="report-period-label"
                            value={preset}
                            label="Period"
                            onChange={handlePresetChange}
                        >
                            <MenuItem value="days">Days</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                            <MenuItem value="custom">Choose dates</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        label="Start date"
                        type="date"
                        value={dateRange.start_date}
                        onChange={(event) => {
                            setPreset('custom')
                            setDateRange((range) => ({ ...range, start_date: event.target.value }))
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        label="End date"
                        type="date"
                        value={dateRange.end_date}
                        onChange={(event) => {
                            setPreset('custom')
                            setDateRange((range) => ({ ...range, end_date: event.target.value }))
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>

                {loadingRequests.size > 0 && <LoadingScreen />}
                <UserFlowDfd rows={userFlowEvents} />
                <NewVsReturningUsers {...reportProps} />

                <Grid container spacing={2} mt={0}>
                    <GoogleAlalytics {...reportProps} />
                    <LandingPageSessions {...reportProps} />
                </Grid>
                <Grid container spacing={2} mt={0}>
                    <NewUsersByChannel {...reportProps} />
                    <ActiveUsersByOS {...reportProps} />
                </Grid>
            </div>
        </>
    )
}

export default Reports
